import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { bool, func, shape, number, arrayOf, string } from "prop-types";

import { resizableRoute } from "pages/_components/Resizable";
import { actions as layoutActions } from "reducers/layout";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";
import { selectors as debinSelectors } from "reducers/debin";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";

import * as i18nUtils from "util/i18n";
import * as accountUtils from "util/accounts";
import { exportGenericTicketPdfWithSuccessMessage, getGeneralTicketItem } from "util/transaction";

import CredinTransactionData from "pages/transfer/_components/CredinTransactionData";
import ModifyPendingTransactionModal from "pages/transactions/_components/ModifyPendingTransactionModal";
import TransactionTicket from "pages/_components/TransactionTicket";
import { isUxDashboardImprovementsEnabled, resolveEmbeddedV3GoBackPath } from "util/v3ShellLayoutSync";

class CredinPrepareConfirmation extends Component {
    static propTypes = {
        isDesktop: bool.isRequired,
        isSmallDesktop: bool.isRequired,
        dispatch: func.isRequired,
        summary: shape({}).isRequired,
        loggedUser: shape({
            accessToken: string,
            defaultAvatarId: string,
            email: string,
            previousLoginInfo: arrayOf(
                shape({
                    city: string,
                    country: string,
                    date: string,
                    idUser: string,
                    ip: string,
                    latitude: number,
                    longitude: number,
                }),
            ),
        }).isRequired,
        accounts: shape({}).isRequired,
        embeddedInV3Shell: bool,
        lastHrefPathname: string,
    };

    static defaultProps = {
        embeddedInV3Shell: false,
        lastHrefPathname: undefined,
    };

    state = {
        isModalDisplayed: false,
        fetchingDownload: false,
        fetching: false,
    };

    componentDidMount() {
        const { summary, dispatch, loggedUser } = this.props;
        if (!summary?.idTransactionStatus && localStorage.getItem("flow")) {
            dispatch(push(`${JSON.parse(localStorage.getItem("flow"))}`));
        } else {
            dispatch(accountsActions.listAccounts(loggedUser.userId));
        }
        this.applyEmbeddedV3ShellLayout();
    }

    componentDidUpdate(prevProps) {
        const { embeddedInV3Shell, isDesktop, summary, lastHrefPathname } = this.props;
        if (
            embeddedInV3Shell &&
            (prevProps.embeddedInV3Shell !== embeddedInV3Shell ||
                prevProps.isDesktop !== isDesktop ||
                prevProps.summary?.idTransactionStatus !== summary?.idTransactionStatus ||
                prevProps.lastHrefPathname !== lastHrefPathname)
        ) {
            this.applyEmbeddedV3ShellLayout();
        }
    }

    componentWillUnmount() {
        const { embeddedInV3Shell } = this.props;
        if (embeddedInV3Shell) {
            this.clearEmbeddedV3ShellLayout();
        }
    }

    clearEmbeddedV3ShellLayout = () => {
        const { dispatch } = this.props;
        dispatch(layoutActions.setScopes([]));
        dispatch(layoutActions.setTitleKey(""));
        dispatch(layoutActions.setHeaderTitle(""));
        dispatch(layoutActions.setHeaderSubtitle(""));
        dispatch(layoutActions.setGoBackPath(null));
        dispatch(layoutActions.setRightContentMobile(null));
    };

    applyEmbeddedV3ShellLayout = () => {
        const { embeddedInV3Shell, dispatch, isDesktop, lastHrefPathname } = this.props;
        if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
            return;
        }
        dispatch(layoutActions.setScopes(["forms"]));
        if (isDesktop) {
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
            return;
        }
        dispatch(layoutActions.setHeaderTitle(""));
        dispatch(layoutActions.setTitleKey("requestCredin.title"));
        dispatch(layoutActions.setGoBackPath(resolveEmbeddedV3GoBackPath("/requestCredin", lastHrefPathname)));
        dispatch(layoutActions.setRightContentMobile(null));
    };

    centerContentMobile = () => {
        const { isDesktop } = this.props;
        return (
            <div>
                {!isDesktop && (
                    <div className="title-account-header-multiline-background-blue">
                        <h1 style={{ margin: "0.2rem" }}>{i18nUtils.get("requestCredin.title")}</h1>
                    </div>
                )}
            </div>
        );
    };

    getAccountName = (account) => {
        const { productAlias, number: accountNumber, productType, currency } = account;
        return `${productAlias} ${accountUtils.maskedAccountNumber(accountNumber)} ${productType} ${i18nUtils.get(
            `currency.label.${currency}`,
        )}`;
    };

    buttonsDetail = () => {
        const { dispatch } = this.props;

        return [
            {
                label: "credin.confirm.goToRequestCredin",
                bsStyle: "primary",
                onClick: () => dispatch(push(`/requestCredin`)),
            },
        ];
    };

    exportPdf = async (accountName) => {
        this.setState({ fetchingDownload: true });
        const { summary, dispatch } = this.props;

        const ticketBody = !Array.isArray(summary.recipients)
            ? this.buildPdf(summary.recipients, accountName)
            : summary.recipients
                  .map((r) => this.buildPdf(r, accountName))
                  .reduce((acum, element) => acum.concat(element), []);

        const ticketData = {
            ticketName: i18nUtils.get("requestCredin.title"),
            ticketBody,
        };

        await exportGenericTicketPdfWithSuccessMessage(ticketData, dispatch, i18nUtils.get("global.download.success"));

        this.setState({ fetchingDownload: false });
    };

    buildPdf = (recipient, accountName) => {
        const { summary } = this.props;
        const { concept, description } = summary;
        const {
            recipientName,
            recipientDocumentType,
            recipientDocumentNumber,
            amount: { currency, quantity },
            accountNumber,
            cbu,
            localBank,
            reference,
        } = recipient;
        return [
            getGeneralTicketItem("negotiate.transaction.date.label", summary?.submitDateTime, "datefulltime"),
            getGeneralTicketItem(
                `requestCredin.confirm.table.header.ticket`,
                summary?.idTransaction.substring(0, 8).toUpperCase(),
                "string",
            ),
            getGeneralTicketItem("requestCredin.confirm.table.header.debitAccount", accountName, "string"),
            getGeneralTicketItem("credin.transaction.id.credin", summary.idCredin || "", "string"),
            getGeneralTicketItem(
                `requestCredin.confirm.table.header.type${cbu?.startsWith("000") ? "cvu" : "cbu"}`,
                cbu,
                "string",
            ),
            getGeneralTicketItem("requestCredin.confirm.table.header.creditAccount.label", accountNumber, "string"),
            getGeneralTicketItem(
                "requestCredin.confirm.table.header.creditAccount.cuit",
                `${recipientDocumentType} ${recipientDocumentNumber}`,
                "string",
            ),
            getGeneralTicketItem("requestCredin.confirm.table.header.creditAccount.owner", recipientName, "string"),
            getGeneralTicketItem(
                `requestCredin.confirm.table.header.creditAccount.${cbu?.startsWith("000") ? "entity" : "bank"}`,
                `${localBank && localBank !== "000" ? localBank : "-"}`,
                "string",
            ),
            getGeneralTicketItem(
                "requestCredin.confirm.table.header.creditAccount.reference",
                reference || "",
                "string",
            ),
            getGeneralTicketItem(
                "requestCredin.confirm.table.header.amount",
                { quantity, currency },
                "amountFrequency",
            ),
            getGeneralTicketItem(`requestCredin.confirm.table.header.ticket`, recipient?.id, "string"),
            getGeneralTicketItem(
                "requestCredin.confirm.table.header.concept",
                i18nUtils.get(`transfers.concept.${concept}`),
                "string",
            ),
            getGeneralTicketItem("requestCredin.confirm.table.header.description", description || "", "string"),
            { key: "", value: "" },
        ];
    };

    closeModifyModal = () => {
        this.setState({ isModalDisplayed: false });
    };

    openModifyModal = () => {
        this.setState({ isModalDisplayed: true });
    };

    render = () => {
        const { isDesktop, summary, accounts, isSmallDesktop, dispatch, embeddedInV3Shell } = this.props;
        const { fetchingDownload, fetching } = this.state;
        let accountName = null;
        if (summary) {
            const numberAccount = summary.account;

            if (accounts.length > 0) {
                const loggedUserAccount = accounts.find((account) => numberAccount.includes(account?.idProduct));
                accountName = accountUtils.getIncomingDebinAccountName(loggedUserAccount);
            }
        }

        const isPending = summary?.idTransactionStatus === "PENDING";
        const { isModalDisplayed } = this.state;
        const buttonsDetail = isPending
            ? [
                  {
                      label: "global.modify",
                      bsStyle: "outline",
                      onClick: this.openModifyModal,
                  },
              ]
            : [
                  {
                      label: "credin.confirm.goToRequestCredin",
                      bsStyle: "primary",
                      onClick: () => dispatch(push(`/requestCredin`)),
                  },
              ];

        return (
            <>
                {summary && (
                    <TransactionTicket
                        defaultConfiguration
                        handleClick={accountName ? () => this.exportPdf(accountName) : null}
                        notificationScope="forms"
                        headerClassName={!isDesktop ? "blue-main-header-mobile blue-main-title-mobile" : ""}
                        centerElement={this.centerContentMobile}
                        hideMobileMenu={!isDesktop}
                        summary={{ ...summary, useTransactionData: true }}
                        isDesktop={isDesktop}
                        isSmallDesktop={isSmallDesktop}
                        generateSignFunction
                        buttonsDetail={buttonsDetail}
                        fetching={fetching}
                        cancelButton
                        summaryTitle="requestCredin.title"
                        isFetchingExport={fetchingDownload}
                        embeddedInV3Shell={embeddedInV3Shell}>
                        <ModifyPendingTransactionModal
                            isDesktop={isDesktop}
                            isDisplayed={isModalDisplayed}
                            handleDismiss={this.closeModifyModal}
                            dispatch={dispatch}
                            idTransaction={summary?.idTransaction}
                        />
                        {Array.isArray(summary.recipients) &&
                            summary.recipients.map((recipient) => (
                                <CredinTransactionData
                                    summary={summary}
                                    isDesktop={isDesktop}
                                    recipient={recipient}
                                    accountName={accountName}
                                />
                            ))}
                        {!Array.isArray(summary.recipients) && (
                            <CredinTransactionData
                                summary={summary}
                                isDesktop={isDesktop}
                                creationDate
                                recipient={summary.recipients}
                                accountName={accountName}
                            />
                        )}
                    </TransactionTicket>
                )}
            </>
        );
    };
}

const mapStateToProps = (state) => ({
    recipients: debinSelectors.getRecipients(state),
    id: debinSelectors.getId(state),
    seller: debinSelectors.getDebinSeller(state),
    fetching: debinSelectors.getFetching(state),
    summary: debinSelectors.getSummary(state),
    loggedUser: sessionSelectors.getUser(state),
    accounts: accountsSelectors.getAccounts(state),
    lastHrefPathname: statusSelectors.getPathname(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(CredinPrepareConfirmation);
